// ---------------------------------------------------------------------------
// themeDerivation — de 6 cores para os 46 tokens de um tema.
//
// Blueprint: `docs/systems/admin-themes.md` §2.
//
// ## Por que derivar, e não pedir 46 campos
//
// Medido nos 26 temas do catálogo: os 8 `--status-*` têm 2 ou 3 valores
// distintos no catálogo INTEIRO, `--lura` tem 5, `--on-lura` 3. O conjunto
// afinado à mão são ~20, e boa parte deles é função dos outros. Um formulário
// de 46 campos não seria autonomia: seria uma planilha em que qualquer
// esquecimento produz tema meio pintado.
//
// ## As fórmulas foram MEDIDAS, não inventadas
//
// Cada regra abaixo foi conferida contra os 26 temas existentes. As que
// reproduzem o catálogo com erro ZERO estão marcadas; as que não reproduzem
// estão marcadas com o que a medição de fato disse, porque em dois casos o
// catálogo não segue regra nenhuma e a derivação escolhe pelo CONTRASTE, que é
// o critério que importa.
// ---------------------------------------------------------------------------

import { composite, contrastRatio, luminance, parseColor, type Rgba } from "./themeContrast";

/** O que o formulário do painel pede. Seis cores, e o resto sai daqui. */
export interface ThemeSeed {
  /** Papel: o fundo do app. */
  surfaceBase: string;
  /** Card: a superfície elevada. Pode ter alfa, e aí o ambiente atravessa. */
  surfaceRaised: string;
  /** Tinta principal. */
  textPrimary: string;
  /** Cor de marca: botão primário, barra de progresso, selo. */
  accent: string;
  /** Segunda cor de marca: detalhes, gradientes. */
  accentSecondary: string;
  /** Linha divisória padrão. */
  borderDefault: string;
  /** Gradiente atrás do app. Opcional — sem ele o tema é chapado. */
  ambient?: string;
}

const hex = (c: Rgba): string =>
  "#" + [c[0], c[1], c[2]].map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, "0")).join("");
const rgba = (c: Rgba, a: number): string =>
  `rgba(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])}, ${a})`;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** Mistura `c` na direção de `alvo`, `p` de 0 a 1. */
const mix = (c: Rgba, alvo: Rgba, p: number): Rgba => [
  c[0] + (alvo[0] - c[0]) * p,
  c[1] + (alvo[1] - c[1]) * p,
  c[2] + (alvo[2] - c[2]) * p,
  1,
];
const BRANCO: Rgba = [255, 255, 255, 1];
const PRETO: Rgba = [0, 0, 0, 1];

/** O tema é escuro? Decidido por luminância, não por palpite de cor. */
export function isDarkSeed(surfaceBase: string): boolean {
  const c = parseColor(surfaceBase);
  return c ? luminance(c) < 0.18 : false;
}

/**
 * Os 8 tokens de status.
 *
 * Medido: no catálogo inteiro cada um tem 2 ou 3 valores distintos, e o grupo
 * dominante cobre a maioria esmagadora. Eles descrevem SEMÂNTICA (sucesso,
 * perigo), não identidade do tema, então derivar por tema seria inventar
 * variação onde o catálogo não tem nenhuma.
 */
const STATUS = {
  "--status-success": "#16a34a",
  "--status-success-bg": "rgba(22, 163, 74, 0.10)",
  "--status-danger": "#dc2626",
  "--status-danger-bg": "rgba(220, 38, 38, 0.10)",
  "--status-warning": "#ca8a04",
  "--status-warning-bg": "rgba(202, 138, 4, 0.10)",
  "--status-info": "#0284c7",
  "--status-info-bg": "rgba(2, 132, 199, 0.10)",
} as const;

/** Alvo AA para texto corrido, espelhado do portão. */
const ALVO_TEXTO = 4.5;

/**
 * A tinta secundária: a mais suave que AINDA passa no AA.
 *
 * ⚠️ Uma fórmula fixa não serve, e a medição mostrou por quê. Misturar 25% na
 * direção do PAPEL parece razoável, mas o texto secundário também é lido sobre
 * o CARD — e nos temas em que card e papel divergem (o `matcha` tem papel
 * `#F2EFE9` e card `#E5E2D9`) a mistura clareia a tinta contra um fundo que
 * ficou mais escuro. Medido: 4,17 sobre o card, abaixo do AA.
 *
 * Então a derivação **verifica e recua**: começa no ponto mais suave e volta em
 * direção à tinta cheia até passar nos dois fundos. Sempre termina, porque a
 * tinta cheia é, por construção, o valor de maior contraste disponível.
 *
 * Isto é o que separa "aplicar fórmula" de "derivar": a fórmula é o ponto de
 * partida, o contraste é o critério de parada.
 */
export function pickSecondary(ink: Rgba, base: Rgba, raised: Rgba): Rgba {
  const fundoCard = raised[3] < 1 ? composite(raised, base) : raised;
  for (let p = 0.25; p > 0; p -= 0.05) {
    const c = mix(ink, base, p);
    if (contrastRatio(c, base) >= ALVO_TEXTO && contrastRatio(c, fundoCard) >= ALVO_TEXTO) return c;
  }
  return ink;
}

/**
 * A tinta que vai SOBRE o preenchimento do accent.
 *
 * Ordem de preferência, e ela não é arbitrária:
 *
 *  1. o **vocabulário do próprio tema** (card ou tinta principal), porque o
 *     botão primário deve parecer parte do tema, não um enxerto;
 *  2. só se nenhum dos dois alcançar o AA, cai em **branco ou preto**.
 *
 * ⚠️ O passo 2 existe porque a medição achou um caso real: o accent do
 * `midnight` é um rosa (`#d04e6e`) que NÃO carrega texto a 4,5 com nenhuma cor
 * do próprio tema — o melhor que ele consegue é 4,40. Sem a queda universal, a
 * derivação produziria um botão primário ilegível e o portão barraria o tema
 * inteiro, sem o operador ter como consertar (a tinta não é campo do
 * formulário). Preferir o tema e só então abrir mão é o que mantém coesão sem
 * abrir mão da legibilidade.
 */
export function pickOnAccent(accent: Rgba, raised: Rgba, ink: Rgba): Rgba {
  const doTema = [raised, ink].sort((a, b) => contrastRatio(b, accent) - contrastRatio(a, accent));
  if (contrastRatio(doTema[0], accent) >= ALVO_TEXTO) return doTema[0];
  const universal = [BRANCO, PRETO].sort(
    (a, b) => contrastRatio(b, accent) - contrastRatio(a, accent),
  );
  // Se nem branco nem preto alcançam, o accent é inviável como preenchimento —
  // devolve o melhor mesmo assim e deixa o PORTÃO reprovar. Silenciar aqui
  // esconderia do operador qual é o campo que ele precisa mudar.
  return contrastRatio(universal[0], accent) > contrastRatio(doTema[0], accent)
    ? universal[0]
    : doTema[0];
}

/**
 * Deriva o conjunto completo de tokens a partir da semente.
 *
 * Devolve `null` se alguma cor da semente não parseia — inventar uma queda aqui
 * produziria um tema plausível a partir de entrada inválida, que é a pior
 * combinação possível.
 */
export function deriveThemeTokens(seed: ThemeSeed): Record<string, string> | null {
  const base = parseColor(seed.surfaceBase);
  const raised = parseColor(seed.surfaceRaised);
  const ink = parseColor(seed.textPrimary);
  const accent = parseColor(seed.accent);
  const accent2 = parseColor(seed.accentSecondary);
  const border = parseColor(seed.borderDefault);
  if (!base || !raised || !ink || !accent || !accent2 || !border) return null;

  const escuro = luminance(base) < 0.18;
  // No escuro os estados clareiam; no claro, escurecem. É o que dá a sensação
  // de "mais forte" nos dois casos.
  const rumo = escuro ? BRANCO : PRETO;

  // A tinta sobre o preenchimento do accent: a que contrastar MAIS entre o card
  // e a tinta do próprio tema.
  //
  // ⚠️ Medido contra os 26: esta regra reproduz a cor exata em só 6 deles, MAS
  // empata no que importa — 1 reprovação de AA com a regra, 1 com os valores
  // hand-tuned. Ou seja: o catálogo não segue regra nenhuma aqui, e escolher
  // pelo contraste é melhor que imitar. Não inventa cor nova: usa o vocabulário
  // do próprio tema.
  const onAccent = pickOnAccent(accent, raised, ink);

  return {
    // ── superfícies ──────────────────────────────────────────────────────────
    "--surface-base": seed.surfaceBase,
    "--surface-raised": seed.surfaceRaised,
    // Overlay é folha e modal: precisa ler sobre QUALQUER coisa, inclusive
    // sobre o pico do ambiente, então nunca herda o alfa do card.
    "--surface-overlay": hex(raised),
    // Exato nos 26 medidos.
    "--surface-inverted": seed.textPrimary,

    // ── texto ────────────────────────────────────────────────────────────────
    "--text-primary": seed.textPrimary,
    // Conservador de propósito (25% na direção do papel): o portão de contraste
    // roda DEPOIS, e um secundário que reprova não tem como ser consertado pelo
    // operador, porque ele não é campo do formulário.
    "--text-secondary": hex(pickSecondary(ink, base, raised)),
    "--text-muted": rgba(ink, 0.62),
    "--text-on-accent": hex(onAccent),
    // Exato nos 26 medidos.
    "--text-inverted": seed.surfaceBase,

    // ── bordas ───────────────────────────────────────────────────────────────
    "--border-subtle": rgba(ink, 0.06),
    "--border-default": seed.borderDefault,
    "--border-strong": rgba(ink, 0.22),
    // Exato em 21 dos 26.
    "--border-focus": seed.accent,

    // ── accent ───────────────────────────────────────────────────────────────
    "--accent-brand": seed.accent,
    "--accent-brand-hover": hex(mix(accent, rumo, 0.12)),
    "--accent-brand-pressed": hex(mix(accent, rumo, 0.24)),
    "--accent-secondary": seed.accentSecondary,

    ...STATUS,

    // ── modo estrutural ──────────────────────────────────────────────────────
    // ⚠️ Três destes não têm consumidor no app (medido: só `--mode-blur` é
    // lido, pelo `Sheet`). Saem preenchidos por coerência com os 26 blocos do
    // código, não porque pintam. Ver `admin-themes.md` §6.1.
    "--mode-blur": "0px",
    "--mode-border-opacity": escuro ? "0.08" : "0.12",
    "--mode-card-shadow": `0 1px 3px ${rgba(escuro ? PRETO : ink, 0.1)}`,
    "--mode-surface-style": raised[3] < 1 ? "veiled" : "solid",

    // ── espelhos legados (`theme.css`) — exatos nos 26 medidos ───────────────
    "--bg-primary": seed.surfaceBase,
    "--bg-surface": hex(raised),
    "--border-color": seed.borderDefault,
    "--brand-primary": seed.accent,
    "--brand-secondary": seed.accentSecondary,
    "--text-secondary-legacy": hex(pickSecondary(ink, base, raised)),
    "--font-serif": '"Fraunces", Georgia, serif',
    "--font-sans": '"Inter", sans-serif',

    // ── papéis do redesign (`redesign-tokens.css`) ───────────────────────────
    // `--accent-strong` tem DEVER DE CONTRASTE em texto, então ele vai na
    // direção contrária do papel, sempre.
    "--accent-strong": hex(mix(accent, escuro ? BRANCO : PRETO, 0.14)),
    "--accent-soft": rgba(accent, escuro ? 0.16 : 0.12),
    "--on-accent": hex(onAccent),
    // A Lura tem identidade PRÓPRIA e não acompanha o tema: ela é o roxo da IA
    // em todo o app. Medido: 5 valores distintos em 26, agrupados por claro e
    // escuro. Derivar do accent faria o selo da Lura mudar de marca por tema.
    "--lura": escuro ? "#cbb6f7" : "#6f4ae6",
    "--on-lura": escuro ? "#241a3a" : "#ffffff",
    "--lura-soft": escuro ? "rgba(170, 140, 250, 0.16)" : "#efeafe",
    "--gold": escuro ? "#d9b872" : "#a86f1d",
    "--line-soft": hex(mix(border, base, 0.5)),
    "--shadow-tint": escuro ? "rgba(0, 0, 0, 0.6)" : rgba(ink, 0.3),

    // ── camada ambiente (opcional) ───────────────────────────────────────────
    ...(seed.ambient ? { "--theme-ambient": seed.ambient } : {}),
  };
}
